import React from 'react';
import { SelectFilter as SelectFilterType } from '../types';
import { useFilter } from '../FilterContext';
import { cn } from '../../../../lib/utils';

import { useTranslation } from 'react-i18next';

interface SelectFilterProps {
  filter: SelectFilterType;
  className?: string;
}

export const SelectFilter: React.FC<SelectFilterProps> = ({
  filter,
  className,
}) => {
  const { t } = useTranslation('pleri/dashboard');
  const { value, update, clear, isActive } = useFilter(filter.id);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    if (selectedValue === '') {
      clear();
    } else {
      // Try to parse as number if it looks like a number
      const numValue = Number(selectedValue);
      update(isNaN(numValue) ? selectedValue : numValue);
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {filter.label}
        {filter.required && <span className="ml-1 text-red-500">*</span>}
      </label>
      <div className="relative">
        <select
          value={(value as string) || ''}
          onChange={handleChange}
          className={cn(
            'w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm dark:border-gray-600',
            'focus:border-[#FF732E] focus:ring-2 focus:ring-[#FF732E] focus:outline-none',
            'appearance-none bg-white text-sm dark:bg-gray-900',
            'pr-8',
            isActive && 'border-[#FF732E] bg-orange-50 dark:bg-gray-800',
          )}
        >
          {!filter.required && (
            <option value="">
              {filter.placeholder || t('selectAnOption')}
            </option>
          )}
          {filter.options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>

        {/* Custom dropdown arrow */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <svg
            className="h-4 w-4 text-gray-400 dark:text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
      {filter.placeholder && !filter.required && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {filter.placeholder}
        </p>
      )}
    </div>
  );
};
